import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useCurrentOrgId } from './AuthContext'
import {
  labelsService,
  LabelKey,
  LabelPair,
  LabelsResponse,
  StatusEntityType,
  StatusLabel,
  StatusLabelsResponse,
} from '../services/labels'

const GENERIC_FALLBACK: Record<LabelKey, LabelPair> = {
  organization: { singular: 'Organization', plural: 'Organizations' },
  portfolio: { singular: 'Portfolio', plural: 'Portfolios' },
  program: { singular: 'Program', plural: 'Programs' },
  project: { singular: 'Project', plural: 'Projects' },
  task: { singular: 'Task', plural: 'Tasks' },
  milestone: { singular: 'Milestone', plural: 'Milestones' },
  department: { singular: 'Department', plural: 'Departments' },
  team: { singular: 'Team', plural: 'Teams' },
  member: { singular: 'Member', plural: 'Members' },
}

// Mirror of GENERIC_STATUS_LABELS in backend/app/core/industry_presets.py so
// rendering doesn't have to wait on the network round-trip.
const GENERIC_STATUS_FALLBACK: Record<StatusEntityType, Record<string, StatusLabel>> = {
  project: {
    planning:  { label: 'Planning',  color: '#6B7280' },
    active:    { label: 'Active',    color: '#10B981' },
    on_hold:   { label: 'On Hold',   color: '#F59E0B' },
    completed: { label: 'Completed', color: '#3B82F6' },
    cancelled: { label: 'Cancelled', color: '#EF4444' },
  },
  task: {
    todo:        { label: 'To Do',       color: '#6B7280' },
    in_progress: { label: 'In Progress', color: '#3B82F6' },
    review:      { label: 'Review',      color: '#F59E0B' },
    done:        { label: 'Done',        color: '#10B981' },
    blocked:     { label: 'Blocked',     color: '#EF4444' },
  },
  milestone: {
    planning:    { label: 'Planning',    color: '#6B7280' },
    in_progress: { label: 'In Progress', color: '#3B82F6' },
    on_hold:     { label: 'On Hold',     color: '#F59E0B' },
    completed:   { label: 'Completed',   color: '#10B981' },
    cancelled:   { label: 'Cancelled',   color: '#EF4444' },
  },
  portfolio: {
    planning:  { label: 'Planning',  color: '#6B7280' },
    active:    { label: 'Active',    color: '#10B981' },
    on_hold:   { label: 'On Hold',   color: '#F59E0B' },
    completed: { label: 'Completed', color: '#3B82F6' },
    cancelled: { label: 'Cancelled', color: '#EF4444' },
    archived:  { label: 'Archived',  color: '#9CA3AF' },
  },
  program: {
    planning:  { label: 'Planning',  color: '#6B7280' },
    active:    { label: 'Active',    color: '#10B981' },
    on_hold:   { label: 'On Hold',   color: '#F59E0B' },
    completed: { label: 'Completed', color: '#3B82F6' },
    cancelled: { label: 'Cancelled', color: '#EF4444' },
    archived:  { label: 'Archived',  color: '#9CA3AF' },
  },
}

type StatusMap = Record<StatusEntityType, Record<string, StatusLabel>>

interface LabelsContextValue {
  industry: string | null
  labels: Record<LabelKey, LabelPair>
  statuses: StatusMap
  loading: boolean
  label: (key: LabelKey, form?: 'singular' | 'plural') => string
  status: (entityType: StatusEntityType, key: string | null | undefined) => StatusLabel
  refresh: () => Promise<void>
  setFromResponse: (resp: LabelsResponse) => void
  setStatusesFromResponse: (resp: StatusLabelsResponse) => void
}

const LabelsContext = createContext<LabelsContextValue | undefined>(undefined)

function mergeStatuses(
  resp: StatusLabelsResponse,
  current: StatusMap,
): StatusMap {
  const next: StatusMap = { ...current }
  for (const entityType of Object.keys(resp.statuses) as StatusEntityType[]) {
    const incoming = resp.statuses[entityType]
    if (!incoming) continue
    next[entityType] = { ...GENERIC_STATUS_FALLBACK[entityType], ...incoming }
  }
  return next
}

export function LabelsProvider({ children }: { children: React.ReactNode }) {
  const [industry, setIndustry] = useState<string | null>(null)
  const [labels, setLabels] = useState<Record<LabelKey, LabelPair>>(GENERIC_FALLBACK)
  const [statuses, setStatuses] = useState<StatusMap>(GENERIC_STATUS_FALLBACK)
  const [loading, setLoading] = useState(true)

  const setFromResponse = useCallback((resp: LabelsResponse) => {
    setIndustry(resp.industry)
    setLabels({ ...GENERIC_FALLBACK, ...resp.labels })
  }, [])

  const setStatusesFromResponse = useCallback((resp: StatusLabelsResponse) => {
    setStatuses(prev => mergeStatuses(resp, prev))
    if (resp.industry !== undefined && resp.industry !== null) {
      setIndustry(resp.industry)
    }
  }, [])

  // Org-scoped: only fetch once someone is signed in. Fetching on public
  // pages (signup, share links, password reset) got a 401 that bounced
  // logged-out visitors to /login.
  const orgId = useCurrentOrgId()

  const refresh = useCallback(async () => {
    if (orgId == null) {
      setLoading(false)
      return
    }
    try {
      const [labelsResp, statusResp] = await Promise.all([
        labelsService.getLabels(orgId),
        labelsService.getStatusLabels(orgId),
      ])
      setFromResponse(labelsResp)
      setStatuses(prev => mergeStatuses(statusResp, prev))
    } catch {
      // Unauthenticated or pre-onboarding — keep generic fallback silently.
    } finally {
      setLoading(false)
    }
  }, [setFromResponse, orgId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const label = useCallback(
    (key: LabelKey, form: 'singular' | 'plural' = 'singular') =>
      (labels[key] ?? GENERIC_FALLBACK[key])[form],
    [labels],
  )

  const status = useCallback(
    (entityType: StatusEntityType, key: string | null | undefined): StatusLabel => {
      const map = statuses[entityType] ?? GENERIC_STATUS_FALLBACK[entityType]
      const fallback = GENERIC_STATUS_FALLBACK[entityType] ?? {}
      if (!key) return { label: '—', color: '#9CA3AF' }
      return (
        map[key] ??
        fallback[key] ?? {
          // Last-ditch: humanize the raw key so we never show nothing.
          label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          color: '#9CA3AF',
        }
      )
    },
    [statuses],
  )

  const value = useMemo<LabelsContextValue>(
    () => ({
      industry,
      labels,
      statuses,
      loading,
      label,
      status,
      refresh,
      setFromResponse,
      setStatusesFromResponse,
    }),
    [industry, labels, statuses, loading, label, status, refresh, setFromResponse, setStatusesFromResponse],
  )

  return <LabelsContext.Provider value={value}>{children}</LabelsContext.Provider>
}

const FALLBACK_VALUE: LabelsContextValue = {
  industry: null,
  labels: GENERIC_FALLBACK,
  statuses: GENERIC_STATUS_FALLBACK,
  loading: false,
  label: (key, form = 'singular') => GENERIC_FALLBACK[key][form],
  status: (entityType, key) => {
    if (!key) return { label: '—', color: '#9CA3AF' }
    return (
      GENERIC_STATUS_FALLBACK[entityType]?.[key] ?? {
        label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        color: '#9CA3AF',
      }
    )
  },
  refresh: async () => {},
  setFromResponse: () => {},
  setStatusesFromResponse: () => {},
}

export function useLabels() {
  // Degrade gracefully outside a provider — return generic defaults instead of
  // throwing. Lets shared components render in tests/stories without scaffolding.
  return useContext(LabelsContext) ?? FALLBACK_VALUE
}
